import React, { useMemo } from "react";
import type { SearchData } from '../../models/SearchElements';
import { useCurrency } from '../CurrencyContext';
import './ActiveFilterBarStyleSheet.css';

type LookupMap = Record<number, string>;

interface ActiveFiltersBarProps {
    filters: SearchData;
    onChange: (next: SearchData) => void;
    artistLookup?: LookupMap;
    categoryLookup?: LookupMap;
    locationLookup?: LookupMap;
}

interface FilterChip {
    key: string;
    label: string;
    onRemove: () => void;
}

export const ActiveFiltersBar: React.FC<ActiveFiltersBarProps> = ({
    filters,
    onChange,
    artistLookup,
    categoryLookup,
    locationLookup,
}) => {
    const { currency } = useCurrency();

    const chips: FilterChip[] = useMemo(() => {
        const c: FilterChip[] = [];

        const set = (patch: Partial<SearchData>) =>
            onChange({ ...filters, ...patch });

        // Description
        if (filters.description && filters.description.trim().length > 0) {
            c.push({
                key: "description",
                label: `Description: “${filters.description.trim()}”`,
                onRemove: () => set({ description: undefined }),
            });
        }

        // Artists
        if (filters.artistIds && filters.artistIds.length > 0) {
            filters.artistIds.forEach((id) => {
                const name =
                    artistLookup?.[id] ?? `Artist #${id}`;
                c.push({
                    key: `artist-${id}`,
                    label: `Artist: ${name}`,
                    onRemove: () =>
                        set({
                            artistIds: filters.artistIds?.filter((x) => x !== id),
                        }),
                });
            });
        }

        // Categories
        if (filters.categoryIds && filters.categoryIds.length > 0) {
            filters.categoryIds.forEach((id) => {
                const name =
                    categoryLookup?.[id] ?? `Category #${id}`;
                c.push({
                    key: `category-${id}`,
                    label: `Category: ${name}`,
                    onRemove: () =>
                        set({
                            categoryIds: filters.categoryIds?.filter((x) => x !== id),
                        }),
                });
            });
        }

        // Locations
        if (filters.locations && filters.locations.length > 0) {

            filters.locations.forEach((id) => {
                const name =
                    locationLookup?.[id] ?? `Location #${id}`;
                c.push({
                    key: `location-${id}`,
                    label: `Location: ${name}`,
                    onRemove: () =>
                        set({
                            locations: filters.locations?.filter((x) => x !== id),
                        }),
                });
            });
        }

        //Acqusition Date
        const { acquiredFrom, acquiredTo } = filters;
        if (acquiredFrom || acquiredTo) {
            let label = "Acquired: ";
            if (acquiredFrom && acquiredTo) {
                label += `${acquiredFrom} – ${acquiredTo}`;
            } else if (acquiredFrom) {
                label += `from ${acquiredFrom}`;
            } else if (acquiredTo) {
                label += `up to ${acquiredTo}`;
            }

            c.push({
                key: "acquisition-date",
                label,
                onRemove: () => set({ acquiredFrom: null, acquiredTo: null }),
            });
        }

        // Value range
        const { minValue, maxValue } = filters;
        if (minValue != null || maxValue != null) {
            let label = "";

            switch (filters.valuationType) {
                case "acquisition":
                    label += "Acquisition Value: "
                    break;
                case "insurance":
                    label += "Insurance Value: "
                    break;
                case "fairMarketValue":
                    label += "Fair Market Value: "
                    break;
                default:
                    label += "Value: "
                    break;
            }

            if (minValue != null && maxValue != null) {
                label += `${minValue.toLocaleString()} – ${maxValue.toLocaleString()} ${currency}`;
            } else if (minValue != null) {
                label += `≥ ${minValue.toLocaleString()} ${currency}`;
            } else if (maxValue != null) {
                label += `≤ ${maxValue.toLocaleString()} ${currency}`;
            }

            c.push({
                key: "value-range",
                label,
                onRemove: () =>
                    set({
                        minValue: null,
                        maxValue: null,
                    }),
            });
        }

        // Has Images
        if (filters.imageStatus && filters.imageStatus != "any") {
            let labelText = "";

            switch (filters.imageStatus) {
                case "with":
                    labelText += "With Image"
                    break;
                case "without":
                    labelText += "Without Image"
                    break;
                default:
                    labelText += "Any"
                    break;
            }

            c.push({
                key: "imageStatus",
                label: labelText,
                onRemove: () => set({ imageStatus: "any" }),
            });
        }

        // Has Acquisition Value
        if (filters.acquisitionValueStatus && filters.acquisitionValueStatus != "any") {
            let labelText = "";

            switch (filters.acquisitionValueStatus) {
                case "with":
                    labelText += "With Acquisition Value"
                    break;
                case "without":
                    labelText += "Without Acquisition Value"
                    break;
                default:
                    labelText += "Any"
                    break;
            }

            c.push({
                key: "acquisitionValueStatus",
                label: labelText,
                onRemove: () => set({ acquisitionValueStatus: "any" }),
            });
        }

        // Has insurance Value
        if (filters.insuranceValueStatus && filters.insuranceValueStatus != "any") {
            let labelText = "";

            switch (filters.insuranceValueStatus) {
                case "with":
                    labelText += "With Insurance Value"
                    break;
                case "without":
                    labelText += "Without Insurance Value"
                    break;
                default:
                    labelText += "Any"
                    break;
            }

            c.push({
                key: "insuranceValueStatus",
                label: labelText,
                onRemove: () => set({ insuranceValueStatus: "any" }),
            });
        }

        // Has Fair Market Value
        if (filters.fairMarketValueStatus && filters.fairMarketValueStatus != "any") {
            let labelText = "";

            switch (filters.fairMarketValueStatus) {
                case "with":
                    labelText += "With FMV"
                    break;
                case "without":
                    labelText += "Without FMV"
                    break;
                default:
                    labelText += "Any"
                    break;
            }

            c.push({
                key: "fairMarketValueStatus",
                label: labelText,
                onRemove: () => set({ fairMarketValueStatus: "any" }),
            });
        }

        return c;
    }, [filters, onChange, artistLookup, categoryLookup, locationLookup]);

    if (chips.length === 0) return null;

    const clearAll = () =>
        onChange({
            ...filters,
            description: undefined,
            artistIds: undefined,
            categoryIds: undefined,
            locations: undefined,
            acquiredFrom: null,
            acquiredTo: null,
            minValue: null,
            maxValue: null,
            imageStatus: "any",
            acquisitionValueStatus: "any",
            insuranceValueStatus: "any",
            fairMarketValueStatus: "any",
        });

    return (
        <div className="active-filters-bar">
            <div className="active-filters-label">Active filters:</div>
            <div className="active-filters-chips">
                {chips.map((chip) => (
                    <button
                        key={chip.key}
                        type="button"
                        className="filter-chip"
                        onClick={chip.onRemove}
                    >
                        <span className="filter-chip-text">{chip.label}</span>
                        <span className="filter-chip-close">×</span>
                    </button>
                ))}
            </div>
            <button
                type="button"
                className="active-filters-clear"
                onClick={clearAll}
            >
                Clear all
            </button>
        </div>
    );
};
